// A track: the commits a range can start or end at, as the range timeline
// draws them, whatever produced them (docs/design.md "Subjects"). A base,
// then a main lane oldest first; commits replaced by force-pushes on side
// lanes, each forking off the point it hangs from; breaks where a
// force-push cut the main lane. A pull request's timeline is one source
// (logic/pullTrack.ts), a compare another (logic/compareTrack.ts), a
// branch's history a third.
//
// Every track has a base: where a comparison from the base starts. A side
// lane hangs from the main point (or base) it forks off; one forking off a
// commit the track does not hold says so (`unheld`), rather than hanging
// from the wrong point. A track may hold only a window of a longer history
// (`hasOlder`): what reads it never takes the base for the start of history.

/**
 * Git's empty tree: the base of a window that reaches a root commit (see
 * `BasePoint`). It is no commit; whatever reads a comparison from it takes
 * every file as added and asks GitHub nothing about it. GitHub's compare
 * takes commits, not trees, so a source whose base is the empty tree
 * lists its base → head files (`DiffSource.files`) from the head's tree:
 * every file, status `added`, with no patch.
 */
export const EMPTY_TREE = '4b825dc642cb6eb9a060e54bf8d69288fbee4904';

/** A commit on a track, with what is known of it. */
export interface TrackCommit {
  sha: string;
  /** The SHA as GitHub abbreviates it. */
  short: string;
  /** First line of the commit message, when known. */
  message: string | null;
  author: string | null;
  /** When it was committed, when known. */
  at: string | null;
}

/** A commit a force-push replaced: no longer on the main lane, still diffable. */
export interface ReplacedPoint extends TrackCommit {
  /** Whose force-push replaced it, when known. */
  replacedBy: string | null;
}

/**
 * Where the main lane starts. `sha` has one meaning for every source: a
 * comparison from the base to an end starts at the merge base of `sha` and
 * that end. A pull request's is its base branch's tip (so each end, a
 * replaced one too, starts from what it was built on); a compare's is the
 * merge base of its branches; a branch history's is the parent of the
 * oldest commit the window holds. A compare's `sha` is an ancestor of
 * every main point, so the comparison starts at `sha` itself; a branch
 * history's is of the head, and of every main point of a linear history
 * (one listed by date across merges may not descend from it). A window
 * reaching the repository's root commit, which has no parent, has git's
 * empty tree (`EMPTY_TREE`) for its base: "All" is everything from nothing,
 * and the merge base of the empty tree with any commit is the empty tree.
 */
export interface BasePoint {
  sha: string;
  /** What the base is called: the base branch's name, or (a window's start) the short SHA, or (the empty tree) what the source says. */
  name: string;
  /** The replaced commits forking off the base, oldest first. */
  replaced: readonly ReplacedPoint[];
}

export interface MainPoint extends TrackCommit {
  /**
   * The main lane breaks before this point: a force-push came before it,
   * or (a compare holding its newest commits) commits between it and the
   * point before are not held.
   */
  breakBefore: boolean;
  /** Who force-pushed this commit as the head, when it came from a force-push. */
  forcePushedBy: string | null;
  /** The replaced commits forking off this point, oldest first. */
  replaced: readonly ReplacedPoint[];
}

/** Replaced commits forking off a commit the track does not hold: older than its window, or never on its main lane. */
export interface UnheldRun {
  /** The commit they fork off, when known. */
  forksOff: string | null;
  /** Oldest first. */
  replaced: readonly ReplacedPoint[];
}

export interface Track {
  base: BasePoint;
  /** The main lane after the base, oldest first. */
  main: readonly MainPoint[];
  /** The head's SHA: the base's or a main point's. */
  head: string;
  /** Replaced runs whose fork point the track does not hold, oldest first. */
  unheld: readonly UnheldRun[];
  /**
   * Older commits than the base exist but are not held (a long history
   * read from the head backwards, a window at a time).
   */
  hasOlder: boolean;
  /**
   * The previous head, when the source knows it (a force-push's before
   * side): where "Last push" starts. Null: the main point before the head.
   */
  lastPush: string | null;
  /** The SHA the viewer's last review was made on, where the subject has reviews and there is one. */
  lastReview: string | null;
}

/** A track with nothing on it but `head` (a subject still loading). */
export function emptyTrack(head = ''): Track {
  return { base: { sha: '', name: 'base', replaced: [] }, main: [], head, unheld: [], hasOlder: false, lastPush: null, lastReview: null };
}
